import React from "react";
import { Avatar, Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

function momentIcon(type) {
  if (type === "reset_submitted" || type === "reset") return <RestartAltRoundedIcon fontSize="small" />;
  if (type === "proof_attached" || type === "proof" || type === "proof_acknowledged") return <ShieldOutlinedIcon fontSize="small" />;
  if (type === "group_all_in" || type === "both_reported" || type === "all_reported") return <GroupsRoundedIcon fontSize="small" />;
  if (type === "comeback" || type === "no_report_recovery") return <AutoAwesomeRoundedIcon fontSize="small" />;
  return <CheckCircleOutlineRoundedIcon fontSize="small" />;
}

function momentActionLabel(type) {
  if (type === "reset_submitted" || type === "reset") return "Open Reset";
  if (type === "daily_report_small_version") return "Open report";
  if (type === "daily_report_done") return "Open report";
  if (type === "proof_attached" || type === "proof") return "Open update";
  if (type === "proof_acknowledged") return "Open update";
  if (type === "group_all_in" || type === "both_reported" || type === "all_reported") return "Open group win";
  if (type === "comeback" || type === "no_report_recovery") return "Open comeback";
  return "Open Moment";
}

function momentTypeLabel(type) {
  if (type === "daily_report_done") return "Daily Report";
  if (type === "daily_report_small_version") return "Reported";
  if (type === "reset_submitted" || type === "reset") return "Reset";
  if (type === "proof_attached" || type === "proof") return "Proof added";
  if (type === "proof_acknowledged") return "Reviewed";
  if (type === "group_all_in") return "Group all-in";
  if (type === "both_reported" || type === "all_reported") return "All reported";
  if (type === "comeback") return "Comeback";
  if (type === "no_report_recovery") return "Recovery";
  return "";
}

function actorName(moment) {
  return moment?.actor?.display_name || moment?.actor?.username || "TrustBud";
}

function MomentCard({ moment, onOpen, onReact }) {
  const chips = Array.from(
    new Set([momentTypeLabel(moment?.type), moment?.status_label, moment?.context_label, moment?.privacy_label].filter(Boolean))
  );
  const canReact = Boolean(moment?.viewer_permissions?.can_react);
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.7,
        borderRadius: "8px",
        border: "1px solid rgba(188, 214, 235, 0.95)",
        bgcolor: "#fff",
      }}
    >
      <Stack spacing={1.2}>
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Avatar src={moment?.actor?.profile_image || ""} sx={{ width: 42, height: 42 }}>
            {actorName(moment).slice(0, 1).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 850, color: "#66758b" }}>
              {actorName(moment)}{moment?.relative_time ? ` · ${moment.relative_time}` : ""}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 17, lineHeight: 1.25, fontWeight: 900, color: "#13233d" }}>
              {moment?.title || "Accountability Moment"}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "8px",
              display: "grid",
              placeItems: "center",
              bgcolor: "#eaf3ff",
              color: "#17345d",
            }}
          >
            {momentIcon(moment?.type)}
          </Box>
        </Stack>
        {moment?.body ? (
          <Typography sx={{ color: "#56667a", fontSize: 14, lineHeight: 1.55 }}>
            {moment.body}
          </Typography>
        ) : null}
        {chips.length ? (
          <Stack direction="row" spacing={0.8} sx={{ flexWrap: "wrap" }}>
            {chips.map((chip) => (
              <Chip
                key={chip}
                size="small"
                label={chip}
                sx={{
                  fontWeight: 850,
                  bgcolor: chip === moment?.privacy_label ? "#eef3f8" : "#f5faff",
                  color: chip === moment?.privacy_label ? "#56667a" : "#17345d",
                }}
              />
            ))}
          </Stack>
        ) : null}
        {moment?.reaction_summary?.length ? (
          <Stack direction="row" spacing={0.8} sx={{ flexWrap: "wrap" }}>
            {moment.reaction_summary.slice(0, 4).map((reaction) => (
              <Button
                key={reaction.reaction_type}
                size="small"
                variant={reaction.viewer_reacted ? "contained" : "outlined"}
                disabled={!canReact}
                onClick={() => onReact(moment, reaction.reaction_type)}
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
              >
                {reaction.label}{reaction.count ? ` ${reaction.count}` : ""}
              </Button>
            ))}
          </Stack>
        ) : null}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={0.7} alignItems="center" sx={{ color: "#66758b" }}>
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
              {Number(moment?.comment_count || 0) ? `${moment.comment_count} comments` : "No comments yet"}
            </Typography>
          </Stack>
          <Button size="small" onClick={() => onOpen(moment)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
            {momentActionLabel(moment?.type)}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function NetworkMomentList({ moments = [], loading = false, onOpen, onReact, onEmptyAction }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "8px",
        border: "1px solid rgba(188, 214, 235, 0.95)",
        bgcolor: "#f8fbff",
      }}
    >
      <Stack spacing={1.4}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 900, color: "#13233d" }}>
            Activity feed
          </Typography>
          <Typography sx={{ mt: 0.35, color: "#56667a", fontSize: 14, lineHeight: 1.5 }}>
            Progress from reports, responses, Resets, and group wins.
          </Typography>
        </Box>
        {moments.length ? (
          <Stack spacing={1.2}>
            {moments.map((moment) => (
              <MomentCard key={moment.id} moment={moment} onOpen={onOpen} onReact={onReact} />
            ))}
          </Stack>
        ) : (
          <Box sx={{ p: 2, borderRadius: "8px", bgcolor: "#fff", border: "1px solid rgba(23, 52, 93, 0.08)" }}>
            <Typography sx={{ fontWeight: 900, color: "#13233d" }}>
              {loading ? "Loading activity..." : "No activity yet"}
            </Typography>
            <Typography sx={{ mt: 0.35, color: "#56667a", fontSize: 14 }}>
              Activity appears when people submit reports, use Reset, add proof, or reach all-in days.
            </Typography>
            {onEmptyAction ? (
              <Button onClick={onEmptyAction} sx={{ mt: 1, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                Find a TrustBud
              </Button>
            ) : null}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
